import { ApiError } from "@/lib/api-client";
import { formatWaitMinutes, readRetryAfterSeconds } from "@/lib/retry-after";
import { toAuthBanner, type AuthBanner } from "@/routes/sign-in/auth-error";

// Maps a thrown register-legacy error to a banner. Branches ONLY on the
// structured `error.code` / `error.status` (never the raw message/details — a
// validation `details` payload can echo input). Duplicate email is no longer
// an error: the backend returns the same 202 acknowledgement as a new signup
// (S-C-01). AUTH-003 is therefore not a designed path; if it still arrives it
// falls through to the generic banner rather than telling the caller the
// address is taken — never an inline field error. Mirrors sign-in/auth-error.ts.
// (It used to name register/register-error.ts as well; that slice was deleted
// by gh-755, which retired the second sign-up form and redirected /register here.)
//
// [Source: src/application/authentication/register_legacy — endpoint.py raises
//  429 AUTH-011 (per-IP registration limiter) and
//  legal_acknowledgement_repository.py raises 503 LEGACY-505.]
//
// The fallback is `toAuthBanner`, whose table holds AUTH-001/002/004/005/503 only,
// so every code this file does not name renders "Something went wrong. Please try
// again." — advice that is actively wrong for a rate limit and for a 503, both of
// which are reachable on THIS endpoint. Anything register-legacy can return and
// sign-in cannot belongs in the branches below, not in the fallback.
export interface LegacySignUpError {
  banner: AuthBanner;
}

/**
 * 429 AUTH-011 — the per-IP registration limiter
 * (`common/exceptions.py` `RegistrationRateLimitedException`, raised by
 * `register_legacy/endpoint.py`). Built rather than tabled for the same reason
 * `mfaVerifyRateLimitedBanner` is: the wait is a value the server sent, which a
 * static record cannot hold. `warn`, matching every other rate-limit banner this
 * descriptor carries.
 *
 * The copy must not say "try again" without saying WHEN — retrying immediately is
 * the one thing that cannot work against a sliding window, and it is what the
 * generic fallback ("Something went wrong. Please try again.") was inviting.
 *
 * Today the duration is usually absent: the endpoint sends the seconds in the
 * `Retry-After` HEADER only, and `ApiError` exposes code/status/details, so
 * `readRetryAfterSeconds` reads `details.retry_after_s` and finds nothing. That is
 * handled rather than assumed — a null wait falls back to the undated sentence,
 * and the dated one starts working the day the envelope carries the field, exactly
 * as the ORG-013 / AUTH-015 / AUTH-016 twins do.
 *
 * Seconds up to 90s, whole minutes above — `auth-error.ts`'s rounding for the
 * same shape of window.
 */
function registrationRateLimitedBanner(details: unknown): AuthBanner {
  const seconds = readRetryAfterSeconds(details);
  if (seconds === null) {
    return {
      variant: "warn",
      message: "Too many sign-up attempts. Please wait before trying again.",
    };
  }
  const wait = seconds <= 90 ? `${Math.ceil(seconds)} seconds` : formatWaitMinutes(seconds);
  return {
    variant: "warn",
    message: `Too many sign-up attempts. Please try again in about ${wait}.`,
  };
}

// 503 LEGACY-505 — the legal-document catalogue could not be resolved
// (`register_legacy/legal_acknowledgement_repository.py`
// `LegalDocumentsUnavailableError`). A SERVER fault, never the caller's, and the
// refusal is raised BEFORE any row is written, so the reassurance is factually
// exact: no account, no organization, no outbox message was left behind. Reuses
// the "temporarily unavailable" posture AUTH-503 already takes on the sign-in
// screen, and never echoes the server's own `details` (which names the catalogue).
const LEGAL_DOCUMENTS_UNAVAILABLE_BANNER: AuthBanner = {
  variant: "warn",
  message:
    "Sign-up is temporarily unavailable. No account was created — please try " +
    "again in a few minutes.",
};

export function toLegacySignUpError(error: unknown): LegacySignUpError {
  if (error instanceof ApiError) {
    // Before the code table: the wait is read off the envelope the server sent.
    if (error.code === "AUTH-011") {
      return { banner: registrationRateLimitedBanner(error.details) };
    }
    if (error.code === "LEGACY-505") {
      return { banner: LEGAL_DOCUMENTS_UNAVAILABLE_BANNER };
    }
    // Story 16.8: the server refused because one of the two legal
    // acknowledgements was absent. Named separately from the generic 422 below
    // because the remedy is specific and actionable — tick the boxes — and the
    // generic "check your details" would send the clinician hunting through the
    // name and email fields instead.
    if (error.code === "LEGACY-004") {
      return {
        banner: {
          variant: "warn",
          message:
            "Please tick both acknowledgements before creating your account.",
        },
      };
    }
    // A server-side validation error the client Zod schema did not pre-catch. We
    // never echo `error.details`; surface a generic prompt to re-check the fields.
    if (error.status === 422) {
      return {
        banner: { variant: "warn", message: "Please check your details and try again." },
      };
    }
  }
  // AUTH-503 / NETWORK_ERROR / unknown → shared mapper (warn/generic, no internals).
  // AUTH-003 (duplicate email) is not designed anymore — it lands here as the
  // generic banner, never an "account already exists" field error.
  return { banner: toAuthBanner(error) };
}
